import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import styled from 'styled-components';
import {
  darkBlueBackground,
  darkBlueBorder, red,
  yellow,
} from '../components/colors';
import { Button } from '../ui/Button';
import { StripeCheckout } from './StripeCheckout';
import { PurchaseType } from './PurchaseContainer';
import {
  useGetExtraUserPropertiesQuery,
  useStripeCustomerGetNextInvoiceQuery,
  useStripeListPackagesQuery,
} from '../queries';
import type { StripeProduct } from '@backend/data/resource';
import currency from 'currency.js';
import { ReactComponent as CheckCircle } from "./../assets/fi_check-circle.svg"
import { ReactComponent as PlusIcon } from "./../assets/plusicon.svg"
import { ReactComponent as CheckIcon } from './../assets/fi_check.svg';
import { ReactComponent as CheckIconDisabled } from './../assets/fi_check_disabled.svg';
import { Spin } from 'antd';


const PackageList = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: nowrap;
  width: 100%;
`;

const PackageWrapper = styled.div<{ selected?: boolean, isStatic?: boolean }>`
  background: ${({ selected } ) => selected ? 'rgba(255,255,255,0.16)' : 'transparent'};
  transition: background-color;
  transition-duration: 200ms;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid ${yellow};
  min-width: 180px;
  width: 100%;
  position: relative;
  cursor: ${({ isStatic }) => isStatic ? 'default' : 'pointer'};
  
  .description {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${yellow};
    font-size: 1rem;
    gap: 7px;
  }
`;

const PackageBadgeWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  justify-content: center;
`

const PackageBadge = styled.div<{ variant: 'primary' | 'secondary' | 'danger' }>`
  background: ${({ variant } ) => variant === 'primary' ? yellow : variant === 'secondary' ? '#0C1426' : '#E83453'};
  color: ${({ variant }) => variant === 'primary' ? '#000' : '#fff'};
  height: 20px;
  padding: 0px 8px;
  font-size: 0.8rem;
  border-radius: 4px;
  font-weight: 500;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
`

const PriceBox = styled.div`
  background: #14234366;
  color: #fff;
  height: 50px;
  font-weight: bold;
  line-height: 50px;
  text-align: center;
  font-size: 1.2rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .period {
    padding-left: 6px;
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
  }
`

const MotionDiv = motion.div

const CheckBoxButton = styled(MotionDiv)<{ selected?: boolean }>`
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  border: 2px solid #fff;
  background: ${({ selected }) => selected ? yellow : darkBlueBackground};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

export type Plan = {
  id: string
  stripePackage?: any
  billingPeriod: 'month' | 'year'
  name: string
  basicPrice: string
  proPrice: string
  description: string
  descriptionUpgrade?: string
  customerType: Array<'new' | 'existing' | 'early-bird'>
}

const _plans: Plan[] = [
  {
    id: 'basic',
    billingPeriod: 'month',
    name: 'Monthly Plan',
    basicPrice: '',
    proPrice: '',
    description:
      'Lifetime access to basic features, no recurring fees',
    customerType: ['new'],
  },
  {
    id: 'pro',
    billingPeriod: 'month',
    name: 'Monthly Subscription',
    basicPrice: '',
    proPrice: '',
    description: '2 weeks free access to all features, then:',
    descriptionUpgrade: 'Access to all features',
    customerType: ['existing', 'early-bird'],
  },
  {
    id: 'pro',
    billingPeriod: 'year',
    name: 'Yearly Subscription',
    basicPrice: '',
    proPrice: '',
    description: '2 weeks free access to all features, then:',
    descriptionUpgrade: 'Access to all features',
    customerType: ['existing', 'early-bird'],
  },
];
export const PurchaseFlow: FC<{
  onStepChanged: (step: 'package' | 'checkout') => void
  onCancel: () => void,
  purchaseType: PurchaseType
}> = ({ onStepChanged, onCancel, purchaseType }) => {
  const [purchaseStep, setPurchaseStep] = useState<'package' | 'checkout'>(
    'package',
  );
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const onPlanSelect = useCallback(
    (plan: Plan) => {
      console.log('Selected plan:', plan);
      setSelectedPlan({
        ...plan,
        id: purchaseType === 'new' ? 'basic' : 'pro'
      });

    },
    [setSelectedPlan, setPurchaseStep, purchaseType],
  );

  const onCheckout = useCallback(() => {
    if (selectedPlan) {
      setPurchaseStep('checkout');
    }
  }, [selectedPlan, setPurchaseStep]);

  const {
    data: stripePackages,
  } = useStripeListPackagesQuery({ enabled: true });

  const { data: extraParams, isLoading: extraIsLoading } = useGetExtraUserPropertiesQuery({ enabled: true })
  const { data: nextInvoiceData, isLoading: nextInvoiceLoading } = useStripeCustomerGetNextInvoiceQuery({ enabled: true });

  useEffect(() => {
    onStepChanged(purchaseStep);
  }, [purchaseStep, onStepChanged]);

  const basicPlans = useMemo(() => {
    if (!stripePackages || stripePackages.length === 0) {
      return [];
    }
    const availablePlansByPurchaseType = _plans.filter(plan => {
      if (purchaseType === 'new') {
        return plan.customerType.includes('new');
      }
      return false;
    });
    const stripBasicPlans = stripePackages.filter((stripePackage: StripeProduct) => {
      return stripePackage.internalId === 'basic';
    });

    const plans: Array<Plan | null> = availablePlansByPurchaseType.map(plan => {
      const matchingStripePackage = stripBasicPlans?.[0];
      if (matchingStripePackage) {
        const price = matchingStripePackage.prices?.[0];

        const priceFormatted = price ?
          currency(price.amount, { fromCents: true, symbol: price.currency === 'eur' ? '€' : '$' }).format()
          : '0.00';

        return {
          ...plan,
          id: matchingStripePackage.internalId!,
          basicPrice: priceFormatted,
          proPrice: '',
        };
      }
      return null;
    });

    return plans.filter((plan): plan is Plan => plan !== null);

  }, [stripePackages, _plans]);

  const proPlans = useMemo(() => {
    if (!stripePackages || stripePackages.length === 0 || extraIsLoading || nextInvoiceLoading) {
      return [];
    }
    const availablePlansByPurchaseType = _plans.filter(plan => {
      return !plan.customerType.includes('new');
    });
    const availablePlansByStripe = stripePackages.filter((stripePackage: StripeProduct) => {
      return availablePlansByPurchaseType.some(plan => plan.id === stripePackage.internalId);
    });

    const plans: Array<Plan | null> = availablePlansByPurchaseType.map(plan => {
      const matchingStripePackage = availablePlansByStripe.find(
        (stripePackage: StripeProduct) => stripePackage.internalId === plan.id,
      );
      if (matchingStripePackage) {
        const price = matchingStripePackage.prices.find(price => price?.interval === plan.billingPeriod);

        const _p = {
          ...plan,
          id: matchingStripePackage.internalId!
        };
        const proPackage = stripePackages.find(p => p.internalId === 'pro' && p.prices.some(pr => pr?.interval === plan.billingPeriod));
        if (!proPackage) {
          return null;
        }
        const proPrice = proPackage.prices.find(pr => pr?.interval === plan.billingPeriod);
        _p.proPrice = proPrice ? currency((proPrice.amount - (plan.billingPeriod === 'month' && (extraParams?.isEarlyBird === true && !nextInvoiceData) ? 500 : 0)), { fromCents: true, symbol: proPrice.currency === 'eur' ? '€' : '$' }).format() : '0.00';
        _p.stripePackage = proPrice

        return _p;
      }
      return null;
    });

    return plans.filter((plan): plan is Plan => plan !== null);
  }, [purchaseType, stripePackages, extraIsLoading, nextInvoiceLoading, nextInvoiceData, extraParams]);

  return (
    <div style={{ width: '100%' }}>
      <AnimatePresence>
        {purchaseStep === 'package' && (
          <>
            {!stripePackages || proPlans.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Spin size={'large'} />
                <div style={{ paddingTop: '10px' }}>
                  Loading packages...
                </div>
              </div>
            ) : (
              <div style={{ width: '100%' }}>
                {purchaseType === 'new' && (
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginTop: '-20px' }}>
                    <div style={{ width: '100%', flexShrink: '0', justifyContent: 'center', display: 'flex', alignItems: 'center', paddingBottom: '10px', fontWeight: 'bold' }}>
                      BASIC SUBSCRIPTION
                    </div>
                    <div style={{ flexGrow: '1' }}>
                      <PackageList>
                        <AnimatePresence>
                          {basicPlans && basicPlans.map(plan => (
                            <PackageWrapper isStatic={true} selected={true} key={`${plan.id}-${plan.billingPeriod}`}>
                              <h4 style={{ marginTop: '5px', marginBottom: '12px' }}>
                                One-time payment
                              </h4>
                              <div className={'description'}>
                                <CheckCircle />
                                <span>
                              {purchaseType === 'new' ? plan.description : plan.descriptionUpgrade}
                            </span>
                              </div>
                              <PriceBox style={{ marginTop: '10px', marginBottom: '6px' }}>
                                {plan.basicPrice}
                                <span className={'period'}>
                                /lifetime
                              </span>
                              </PriceBox>
                            </PackageWrapper>
                          ))}
                        </AnimatePresence>
                      </PackageList>
                    </div>
                  </div>
                )}
                { purchaseType === 'new' && (
                  <div className={'divider-with-plusicon'} style={{ display: 'flex', gap: '16px', marginTop: '12px', alignItems: 'center', width: '100%', height: '25px' }}>
                    <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(255,255,255,0.3)' }} />
                    <div style={{ position: 'relative', textAlign: 'center' }}>
                      <PlusIcon style={{ marginTop: '6px', height: '26px', width: '26px'}} />
                    </div>
                    <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(255,255,255,0.3)' }} />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginTop: '10px' }}>
                  <div style={{ width: '100%', flexShrink: '0', justifyContent: 'center', display: 'flex', alignItems: 'center', paddingBottom: '20px', fontWeight: 'bold' }}>
                    PRO SUBSCRIPTION
                  </div>
                  <div style={{ flexGrow: '1' }}>
                    <PackageList>
                      <AnimatePresence>
                        {proPlans.filter(plan => plan !== null).map(plan => (
                          <PackageWrapper selected={selectedPlan?.billingPeriod === plan.billingPeriod} key={`${plan.id}-${plan.billingPeriod}`} onClick={() => onPlanSelect(plan)}>
                            <PackageBadgeWrapper>
                              {plan.billingPeriod === 'month' && (
                                <PackageBadge variant={'primary'}>MOST POPULAR</PackageBadge>
                              )}
                              {plan.billingPeriod === 'month' && (extraParams?.isEarlyBird && !nextInvoiceData) && (
                               <>
                                 <PackageBadge variant={'danger'}>EARLY BIRD SAVE €5</PackageBadge>
                               </>
                              )}
                              {plan.billingPeriod === 'year' && (
                                <>
                                  <PackageBadge variant={'secondary'}>BEST VALUE</PackageBadge>
                                  <PackageBadge variant={'danger'}>SAVE 10%</PackageBadge>
                                </>
                              )}
                            </PackageBadgeWrapper>
                            <h4 style={{ marginTop: '5px', marginBottom: '20px' }}>
                              {plan.billingPeriod === 'month' ? 'Monthly plan' : 'Yearly plan'}
                            </h4>
                            <div style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '7px', paddingLeft: '5px' }}>
                              <CheckCircle />
                              <span>
                                  {purchaseType === 'new' ? plan.description : plan.descriptionUpgrade}
                                </span>
                            </div>
                            <PriceBox style={{ marginTop: '10px', marginBottom: '6px' }}>
                              <>{plan.proPrice}</>
                              <span className={'period'}>
                                {plan.billingPeriod === 'month' ? '/month' : '/year'}
                                { plan.billingPeriod === 'month' && (extraParams?.isEarlyBird && !nextInvoiceData) && (
                                  <> for 3 months</>
                                )}
                              </span>
                            </PriceBox>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                              <CheckBoxButton selected={selectedPlan?.billingPeriod === plan.billingPeriod}>
                                {selectedPlan?.billingPeriod === plan.billingPeriod ? (
                                    <CheckIcon style={{ width: '20px', height: '20px', color: '#fff' }} />
                                  ) :
                                  (
                                    <CheckIconDisabled style={{ width: '20px', height: '20px', color: '#fff' }} />
                                  )
                                }
                              </CheckBoxButton>
                            </div>
                          </PackageWrapper>
                        ))}
                      </AnimatePresence>
                    </PackageList>
                  </div>
                </div>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', gap: '100px' }}>
                  <Button
                    style={{ width: '140px', marginBottom: 0 }}
                    large={true}
                    tertiary={true}
                    onClick={() => {
                      if (onCancel) {
                        onCancel();
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button disabled={!selectedPlan} style={{ marginBottom: 0, flexGrow: 1 }} large={true} onClick={() => onCheckout()}>
                    Buy Now
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {purchaseStep === 'checkout' && selectedPlan && (
          <div>
            <StripeCheckout
              onCancelPaymentProcess={() => {
                setPurchaseStep('package')
              }}
              selectedPlan={selectedPlan}
              planId={selectedPlan.id}
              billingPeriod={selectedPlan.billingPeriod}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
